import { api } from './Api';
import { portalAccess } from './PortalAccess';

export const COVERAGE_COLOR = '#526878';
export const RATIO_LABELS = {
  available: 'Ratio available',
  orders_without_drivers: 'Orders, no mapped drivers',
  no_orders_or_drivers: 'No orders or mapped drivers',
};
export const MAPPING_DIAGNOSTICS = [
  'unmapped_dispatchable_orders', 'missing_pickup_coordinates', 'overlapping_pickup_points',
  'unmapped_clocked_in_drivers', 'missing_active_runs', 'duplicate_active_run_drivers',
  'invalid_run_workload', 'run_assignment_count_mismatches', 'idle_runs_with_current_order',
  'invalid_busy_current_orders', 'missing_driver_coordinates', 'stale_idle_gps',
  'missing_idle_gps_timestamp', 'overlapping_driver_points',
  'missing_idle_device_coordinates', 'missing_idle_run_timestamp',
  'invalid_idle_run_timestamp', 'idle_run_update_older_than_five_minutes',
];
export const DIAGNOSTIC_LABELS = {
  missing_driver_coordinates: 'Missing/invalid mapping coordinates',
  stale_idle_gps: 'Stale/invalid idle device GPS time',
  missing_idle_gps_timestamp: 'Missing idle device GPS time',
  missing_idle_device_coordinates: 'Missing/invalid idle device coordinates',
  missing_idle_run_timestamp: 'Missing idle run update time',
  invalid_idle_run_timestamp: 'Invalid/future idle run update time',
  idle_run_update_older_than_five_minutes: 'Idle run updated over 5 minutes ago',
};
export const canViewCoverage = (user) =>
  portalAccess(user).dispatcher;
export const positiveBusinessId = (id) => /^[1-9]\d*$/.test(String(id)) && Number.isSafeInteger(Number(id));
const count = (n) => Number.isSafeInteger(n) && n >= 0;
const number = (n) => typeof n === 'number' && Number.isFinite(n);
const nonnegative = (n) => number(n) && n >= 0;
const invalid = () => { throw new Error('Invalid coverage response. Refresh or contact support.'); };

// Check snapshot calendar metadata only, never infer order eligibility or counts.
const validBusinessDate = (model, asOf) => {
  if (typeof model.business_timezone !== 'string' || !model.business_timezone.trim() ||
      typeof model.business_local_date !== 'string' ||
      !/^\d{4}-\d{2}-\d{2}$/.test(model.business_local_date)) return false;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: model.business_timezone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(asOf));
    const part = (type) => parts.find((p) => p.type === type).value;
    return model.business_local_date === `${part('year')}-${part('month')}-${part('day')}`;
  } catch {
    return false;
  }
};

export const validCoverageGeometry = (geometry) => {
  if (geometry === null) return true;
  const position = (p) => Array.isArray(p) && p.length === 2 &&
    number(p[0]) && number(p[1]) && Math.abs(p[0]) <= 180 && Math.abs(p[1]) <= 90;
  return geometry?.type === 'MultiPolygon' && Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length > 0 && geometry.coordinates.every((polygon) =>
      Array.isArray(polygon) && polygon.length > 0 && polygon.every((ring) =>
        Array.isArray(ring) && ring.length >= 4 && ring.every(position) &&
        ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]));
};

// Validate the contract, never reconstruct demand or capacity from map markers.
export const validateCoverage = (data) => {
  const model = data?.model;
  if (model?.version !== 'dispatchable_orders_per_driver_v2') {
    throw new Error('Unsupported coverage model. Expected dispatchable_orders_per_driver_v2.');
  }
  if (data?.type !== 'FeatureCollection' ||
      !Number.isSafeInteger(data.business_id) || data.business_id <= 0 ||
      typeof data.as_of_utc !== 'string' || !/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?Z$/.test(data.as_of_utc) ||
      !Number.isFinite(Date.parse(data.as_of_utc)) ||
      model.primary_input !== 'dispatchable_orders_per_driver' ||
      model.pickup_upper_window_minutes !== 40 || model.pickup_lower_bound !== null ||
      model.order_date_scope !== 'delivery_date equals business-local today' ||
      !validBusinessDate(model, data.as_of_utc) ||
      model.dispatchable_status_exclusive_upper_bound !== 6 ||
      !['driver_location_source', 'idle_location_age_source', 'active_run_selection', 'overlap_policy']
        .every((key) => typeof model[key] === 'string') ||
      model.legacy_need_level_source !== 'v1_comparison' || model.severity_bands_enabled !== false ||
      !data.business_summary || !['active_driver_count', 'current_order_count']
        .every((key) => count(data.business_summary[key])) ||
      !data.mapping_diagnostics || !MAPPING_DIAGNOSTICS.every((key) => count(data.mapping_diagnostics[key])) ||
      !Array.isArray(data.features)) invalid();
  const ids = new Set();
  data.features.forEach((feature) => {
    const p = feature?.properties;
    if (feature?.type !== 'Feature' || !validCoverageGeometry(feature.geometry) ||
        !p || !Number.isSafeInteger(p.id) || p.id <= 0 || ids.has(p.id) || typeof p.name !== 'string' ||
        !count(p.active_driver_count) || !count(p.dispatchable_order_count)) invalid();
    const drivers = p.active_driver_count;
    const orders = p.dispatchable_order_count;
    if (drivers > 0) {
      // Consistency check only: always retain and display the server's raw quotient.
      const expected = orders / drivers;
      if (p.ratio_state !== 'available' || !nonnegative(p.dispatchable_orders_per_driver) ||
          (orders === 0 && p.dispatchable_orders_per_driver !== 0) ||
          Math.abs(p.dispatchable_orders_per_driver - expected) >
            Number.EPSILON * Math.max(1, expected) * 4) invalid();
    } else if (p.dispatchable_orders_per_driver !== null ||
        p.ratio_state !== (orders > 0 ? 'orders_without_drivers' : 'no_orders_or_drivers')) invalid();
    ids.add(p.id);
  });
  return data;
};

export const getTerritoryCoverage = async (businessId) => {
  if (!positiveBusinessId(businessId)) throw new Error('Select a positive authorized Business ID.');
  const data = validateCoverage(await api.get('/deliveryops/territory-coverage',
    { business_id: Number(businessId) }, { cache: 'no-store' }));
  if (data.business_id !== Number(businessId)) invalid();
  return data;
};
export const coverageName = (feature) => feature.properties.name.trim() || `Territory ${feature.properties.id}`;
export const coverageRatioLabel = (feature) => feature.properties.ratio_state === 'available'
  ? `${feature.properties.dispatchable_orders_per_driver.toFixed(2)} orders/driver`
  : RATIO_LABELS[feature.properties.ratio_state];
const stateOrder = { orders_without_drivers: 0, available: 1, no_orders_or_drivers: 2 };
export const rankCoverage = (features, ratioState = 'all') =>
  features.filter((f) => ratioState === 'all' || f.properties.ratio_state === ratioState).slice().sort((a, b) => {
    const x = a.properties;
    const y = b.properties;
    return stateOrder[x.ratio_state] - stateOrder[y.ratio_state] ||
      (x.ratio_state === 'available' ? y.dispatchable_orders_per_driver - x.dispatchable_orders_per_driver : 0) ||
      x.id - y.id;
  });
export const coverageErrorMessage = (error) => {
  const label = error?.status === 403 ? 'Unauthorized coverage access. Sign in with an authorized dispatcher account.'
    : error?.status === 400 ? 'Invalid coverage request. Select an authorized business.'
      : 'Coverage unavailable.';
  return `${label}${error?.message ? ` ${error.message}` : ''}${error?.request_id ? ` Request ID: ${error.request_id}` : ''}`;
};
